import { useState } from "react";
import { Share2, Gift, Copy, Twitter, Linkedin, Facebook, Mail, Check } from "lucide-react";

function WhatsAppGlyph({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.019-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

/**
 * Shareable personal-referral card. Display on redeem-success screens and
 * anywhere we want to push the viral loop. `referral` = {code, amount,
 * bonus_on_share} from `/credits/coupons/my-referral`.
 */
export default function ReferralShareCard({ referral, compact = false }) {
  const [copied, setCopied] = useState(false);
  if (!referral?.code) return null;

  const appUrl = typeof window !== "undefined" && window.location
    ? window.location.origin
    : "https://toprankpulse.com";
  const url = `${appUrl}/redeem/${referral.code}`;
  const text = `🎁 Claim ${referral.amount} free Ads Creator credits on TopRankPulse — I vouch for it! Use my code ${referral.code}:`;
  const full = `${text} ${url}`;
  const enc = encodeURIComponent;

  const channels = [
    { key: "twitter", Icon: Twitter, color: "#1da1f2",
      href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}` },
    { key: "linkedin", Icon: Linkedin, color: "#0a66c2",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
    { key: "whatsapp", Icon: WhatsAppGlyph, color: "#25d366",
      href: `https://api.whatsapp.com/send?text=${enc(full)}` },
    { key: "facebook", Icon: Facebook, color: "#1877f2",
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}&quote=${enc(text)}` },
    { key: "email", Icon: Mail, color: "#475569",
      href: `mailto:?subject=${enc("I got you free Ads Creator credits")}&body=${enc(full)}` },
  ];

  async function copyUrl() {
    try { await navigator.clipboard.writeText(url); setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  }

  const padding = compact ? 14 : 18;
  const cardStyle = {
    padding,
    background: "linear-gradient(135deg,rgba(168,85,247,0.14) 0%,rgba(124,58,237,0.14) 100%)",
    border: "1px solid rgba(168,85,247,0.35)",
    borderRadius: 14,
    color: "#fff",
  };

  return (
    <div data-testid="referral-share-card" style={cardStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: "linear-gradient(135deg,#a855f7,#7c3aed)",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          <Gift size={16} color="#fff" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: "-0.01em" }}>
            Share &amp; earn <span style={{ color: "#c084fc" }}>+{referral.bonus_on_share || 10} credits</span>
          </div>
          <div style={{ fontSize: 12, color: "rgba(232,232,240,0.75)", lineHeight: 1.4 }}>
            Your friend gets +{referral.amount}, you get +{referral.bonus_on_share || 10} when they redeem.
            {referral.team_bonus_hint !== false && (
              <span style={{ color: "#4ade80", fontWeight: 700 }}>
                {" "}Team-mates get +5 each. 🏢
              </span>
            )}
          </div>
        </div>
      </div>
      <div style={{
        display: "flex", alignItems: "center", gap: 6,
        padding: "8px 10px",
        background: "rgba(0,0,0,0.35)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 8, marginBottom: 10,
      }}>
        <span style={{
          flex: 1, fontFamily: "monospace", fontSize: 13,
          letterSpacing: "0.08em", color: "#fff", overflow: "hidden", textOverflow: "ellipsis",
        }}>{referral.code}</span>
        <button
          data-testid="referral-copy-btn"
          onClick={copyUrl}
          style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "4px 10px", fontSize: 11, fontWeight: 700,
            border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6,
            background: copied ? "rgba(74,222,128,0.2)" : "rgba(255,255,255,0.08)",
            color: copied ? "#4ade80" : "#fff",
            cursor: "pointer",
          }}
        >
          {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy link</>}
        </button>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <Share2 size={12} style={{ color: "rgba(255,255,255,0.5)" }} />
        {channels.map(({ key, Icon, color, href }) => (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer"
            data-testid={`referral-share-${key}`}
            title={`Share via ${key}`}
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 28, height: 28, borderRadius: 8,
              background: color, color: "#fff", textDecoration: "none",
            }}>
            <Icon size={14} />
          </a>
        ))}
      </div>
    </div>
  );
}
